import React, { memo, useState, useEffect } from "react";
import {
    Box,
    Drawer,
    IconButton,
    Stack,
    useMediaQuery,
    Divider,
    Snackbar,
    Alert,
    Badge,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme as useMuiTheme } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "../../../core/providers/ThemeProvider";
import { colors, shadows } from "../../styles/theme/colors";
import { containerMaxWidths, responsiveSpacing } from "../../styles/theme/theme";
import { responsivePadding } from "../../utils/responsive";
import CustomButton from "../ui/Button/Custombutton";
import { Logo } from "../common/Logo/Logo";
import translations from "../../translations/en.json";
import { getLogger } from "../../../shared/utils/logger";
import BellIcon from "../../../assets/bell.svg";
import DarkBellIcon from "../../../assets/darkNotif.svg";
import { useUserStore } from "../../../app/store/useUserStore";
import { isTravellerRole } from "../../utils/roleUtils";
import UserMenu from "../../../features/User/UserMenu";
import RoleToggle from "../../../shared/components/ui/RoleToggle/RoleToggle";
import { useRoleSwitch } from "../../../features/User/hooks/useRoleSwitch";
import { useInitialRoleSelection } from "../../../features/User/hooks/useInitialRoleSelection";
import RoleSelectionDialog from "../ui/RoleSelectionDialog/RoleSelectionDialog";
import { NotificationDropdown } from "../ui/NotificationDropdown";
import { UserAvatar } from "../ui/Avatar";
import { useSelector, useDispatch } from "react-redux";
import type{ RootState } from "../../../app/store";
import { loadNotifications } from "../../../app/store/slices/notificationSlice";

export const AuthNavbar: React.FC = memo(() => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [bellAnchorEl, setBellAnchorEl] = useState<null | HTMLElement>(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const muiTheme = useMuiTheme();
    const { theme } = useTheme();
    const { isAuthenticated, loginWithRedirect, user: auth0User } = useAuth0();
    const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('lg'));
    const dispatch = useDispatch();

    const { user } = useUserStore();
    const { switchRole, isSwitching, error: roleSwitchError, clearError } = useRoleSwitch();
    const {
        showDialog: showRoleSelectionDialog,
        setInitialRole,
        isSettingRole,
        clearError: clearRoleSelectionError,
    } = useInitialRoleSelection();

    const handleInitialRoleSelect = async (role: 'traveller' | 'sender') => {
        try {
            await setInitialRole(role);
            setTimeout(() => {
                navigate('/update-profile', { replace: true });
            }, 300);
        } catch { /* ignore */ }
    };

    // Get role-based unread count (excluding role_switch and other users' notifications)
    const allNotifications = useSelector((state: RootState) => state.notifications.notifications);
    const currentRole = user?.user_role;
    const currentUserId = user?.uuid || String(user?.user_id || '');
    // Fallback for notifications cached before uuid was populated
    const legacyUserId = user?.user_id !== undefined ? String(user.user_id) : '';
    const unreadCount = (allNotifications || []).filter(n => {
      if (n.isRead) return false;
      if (n.type === 'role_switch') return false;
      if (currentUserId && n.userId && n.userId !== currentUserId && !(legacyUserId && n.userId === legacyUserId)) return false;
      if (currentRole && n.user_role && n.user_role !== currentRole) return false;
      return true;
    }).length;

    // Only check if we're on the landing/home page (/)
    // On landing page: navbar is glassy until user scrolls >10px, then becomes solid
    // On all other pages: navbar is always solid (no glassmorphic effect)
    const isOnLandingPage = location.pathname === '/';

    // Note: User profile is fetched globally by AuthInitializer
    // We just read from the store here - no need to fetch again

    // Load notifications on mount
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(loadNotifications());
        }
    }, [isAuthenticated, dispatch]);

    // Handle scroll effect
    useEffect(() => {
        // Set initial scroll state
        setIsScrolled(window.scrollY > 10);

        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Clear error when mobile menu closes
    useEffect(() => {
        if (!isMobileMenuOpen && roleSwitchError) {
            clearError();
        }
    }, [isMobileMenuOpen, roleSwitchError, clearError]);

    const handleNavigation = (path: string) => {
        navigate(path);
        setIsMobileMenuOpen(false);
    };

    // Handle role switch with the new hook
    const handleRoleToggle = async (newRole: 'traveller' | 'sender') => {
        if (!isAuthenticated) {
            loginWithRedirect({
                appState: { returnTo: location.pathname },
            });
            return;
        }

        const success = await switchRole();

        if (success) {
            setShowSuccessMessage(true);
            setIsMobileMenuOpen(false);
        }
    };

    const handleSignup = () => {
        loginWithRedirect({
            screen_hint: "signup",
            appState: { returnTo: window.location.origin },
        }).catch((err) => {
        });
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setBellAnchorEl(bellAnchorEl ? null : event.currentTarget);
    };

    const handleNotificationClose = () => {
        setBellAnchorEl(null);
    };

    // Background opacity changes on scroll
    // On landing pages: glassy when not scrolled (>10px), solid when scrolled
    // On other pages: always solid
    const shouldUseSolidStyle = !isOnLandingPage || isScrolled;

    const navBackground = shouldUseSolidStyle
        ? colors.glass.white95  // rgba(255, 255, 255, 0.95) - Solid white
        : colors.glass.white30; // rgba(255, 255, 255, 0.30) - Glassy/translucent

    // Logo variant changes based on scroll or page background
    const logoVariant = shouldUseSolidStyle ? 'dark' : 'light';

    // Text color changes based on scroll or page background
    const textColor = shouldUseSolidStyle ? colors.form.label : colors.neutral.white;
    const profileImage = user?.profile_image;

    return (
        <>
            {/* Desktop & Tablet Navigation */}
            <Box
                component="nav"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    width: '100%',
                    maxWidth: `${containerMaxWidths.header}px`,
                    height: { xs: 'auto', lg: '67.23px' },
                    padding: {
                        xs: '0.75rem 1rem',
                        sm: '0.75rem 1.125rem',
                        lg: '0.75rem 1.125rem',
                        xl: '0.75rem 1.25rem',
                        xxl: '0.75rem 1.5rem',
                    },
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: {
                        xs: responsiveSpacing.smallGap.xs,
                        sm: responsiveSpacing.mediumGap.sm,
                        lg: responsiveSpacing.mediumGap.lg,
                        xl: responsiveSpacing.mediumGap.xl,
                        xxl: responsiveSpacing.mediumGap.xxl,
                    },
                    '@media (max-width: 310px) and (max-height: 300px)': {
                        padding: '0.5rem',
                        gap: '4px',
                    },
                    background: navBackground,
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    borderRadius: '16px',
                    transition: 'background 0.3s ease-in-out, box-shadow 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out',
                    boxShadow: shouldUseSolidStyle ? shadows.AuthNavbar : 'none',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                }}
            >
                {/* Logo - Leftmost position */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={() => handleNavigation('/')}
                >
                    <Logo
                        variant={logoVariant}
                        sx={{
                            width: { xs: '100px', sm: '110px', lg: '124.09px', xl: '135px', xxl: '145px' },
                            height: { xs: '29px', sm: '32px', lg: '36px', xl: '39px', xxl: '42px' },
                            '@media (max-width: 310px) and (max-height: 300px)': {
                                width: '72px',
                                height: '21px',
                            },
                        }}
                    />
                </Box>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* Right Side Actions - Desktop Only */}
                {!isSmallScreen && (
                    <Stack
                        direction="row"
                        spacing={{ lg: 1, xl: 1.25, xxl: 1.5 }}
                        sx={{
                            alignItems: 'center',
                            flex: '0 0 auto',
                            minWidth: 'fit-content',
                        }}
                    >
                        {/* Role Toggle Component */}
                        <RoleToggle
                            currentRole={user?.user_role || ""}
                            onChange={handleRoleToggle}
                            disabled={!isAuthenticated}
                            loading={isSwitching}
                        />

                        {isAuthenticated ? (
                            <Stack direction="row" spacing={{ lg: 1, xl: 1.25, xxl: 1.5 }} alignItems="center">

                                {/* Bell Icon */}
                                <IconButton
                                    onClick={handleBellClick}
                                    sx={{
                                        p: { lg: 0.5, xl: 0.625, xxl: 0.75 },
                                        '&:hover': {
                                            backgroundColor: shouldUseSolidStyle
                                                ? 'rgba(0, 0, 0, 0.05)'
                                                : colors.glass.white10,
                                        },
                                    }}
                                    aria-label={translations.navigation.notifications}
                                >
                                    <Badge
                                        badgeContent={Math.min(unreadCount, 30)}
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                backgroundColor: isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056',
                                                color: 'white',
                                                fontSize: { lg: '0.6rem', xl: '0.625rem', xxl: '0.65rem' },
                                                height: { lg: '16px', xl: '17px', xxl: '18px' },
                                                minWidth: { lg: '16px', xl: '17px', xxl: '18px' },
                                                borderRadius: '50%',
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={shouldUseSolidStyle ? DarkBellIcon : BellIcon}
                                            alt={translations.navigation.notifications}
                                            sx={{
                                                width: { lg: 22, xl: 23, xxl: 24 },
                                                height: { lg: 22, xl: 23, xxl: 24 },
                                                filter: unreadCount > 0
                                                    ? `drop-shadow(0 0 2px ${isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056'})`
                                                    : 'none',
                                            }}
                                        />
                                    </Badge>
                                </IconButton>

                                {/* Avatar with Profile Image */}
                                <IconButton
                                    onClick={handleAvatarClick}
                                    sx={{
                                        p: 0,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    aria-label="Profile"
                                >
                                    <UserAvatar
                                        src={profileImage}
                                        name={user?.full_name || user?.name}
                                        email={user?.email || auth0User?.email}
                                        userRole={user?.user_role}
                                        size={32}
                                        fontSize="14px"
                                    />
                                </IconButton>
                            </Stack>
                        ) : (
                            <CustomButton
                                onClick={handleSignup}
                                buttonText={translations.navigation.signup}
                                sx={{
                                    minWidth: { lg: '100px', xl: '110px', xxl: '120px' },
                                    height: { lg: '40px', xl: '42px', xxl: '44px' },
                                    px: { lg: 2, xl: 2.5, xxl: 3 },
                                    backgroundColor: colors.button.primary,
                                    color: colors.neutral.white,
                                    fontSize: { lg: '0.875rem', xl: '0.9375rem', xxl: '1rem' },
                                    fontWeight: 500,
                                    fontFamily: theme.typography.bodyText.fontFamily,
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        backgroundColor: colors.button.primaryHover,
                                    },
                                    transition: 'background-color 0.2s',
                                }}
                            />
                        )}
                    </Stack>
                )}

                {/* Mobile Menu Buttons */}
                {isSmallScreen && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Bell Icon - Only show for authenticated users */}
                        {isAuthenticated && (
                            <IconButton
                                onClick={handleBellClick}
                                sx={{
                                    p: 0.5,
                                    '&:hover': {
                                        backgroundColor: shouldUseSolidStyle
                                            ? 'rgba(0, 0, 0, 0.05)'
                                            : colors.glass.white10,
                                    },
                                }}
                                aria-label={translations.navigation.notifications}
                            >
                                <Badge
                                    badgeContent={Math.min(unreadCount, 30)}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056',
                                            color: 'white',
                                            fontSize: '0.65rem',
                                            height: '18px',
                                            minWidth: '18px',
                                            borderRadius: '9px',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={shouldUseSolidStyle ? DarkBellIcon : BellIcon}
                                        alt={translations.navigation.notifications}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            filter: unreadCount > 0
                                                ? `drop-shadow(0 0 2px ${isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056'})`
                                                : 'none',
                                        }}
                                    />
                                </Badge>
                            </IconButton>
                        )}

                        {/* Hamburger Menu Button */}
                        <IconButton
                            onClick={toggleMobileMenu}
                            sx={{
                                p: 0.5,
                                color: textColor,
                                '&:hover': {
                                    backgroundColor: shouldUseSolidStyle
                                        ? 'rgba(0, 0, 0, 0.05)'
                                        : colors.glass.white10,
                                },
                            }}
                            aria-label="Toggle menu"
                        >
                            {isMobileMenuOpen ? (
                                <CloseIcon sx={{ width: 24, height: 24 }} />
                            ) : (
                                <MenuIcon sx={{ width: 24, height: 24 }} />
                            )}
                        </IconButton>
                    </Stack>
                )}
            </Box>

            {/* User Menu — renders as a dropdown on desktop/tablet and a top Drawer on mobile,
                triggered by either the avatar click (anchorEl) or the hamburger (isMobileMenuOpen). */}
            {isAuthenticated && (
                <UserMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) || (isMobileMenuOpen && isSmallScreen)}
                    onClose={() => {
                        handleMenuClose();
                        setIsMobileMenuOpen(false);
                    }}
                    mobileHeaderSlot={
                        <RoleToggle
                            currentRole={user?.user_role || ""}
                            onChange={handleRoleToggle}
                            disabled={!isAuthenticated}
                            loading={isSwitching}
                        />
                    }
                />
            )}

            {/* Signup prompt — mobile hamburger menu when signed out */}
            {!isAuthenticated && (
                <Drawer
                    anchor="top"
                    open={isMobileMenuOpen && isSmallScreen}
                    onClose={toggleMobileMenu}
                    PaperProps={{
                        style: {
                            marginTop: isSmallScreen ? '80px' : '90px',
                            maxHeight: isSmallScreen ? 'calc(100vh - 80px)' : 'calc(100vh - 90px)',
                            overflowY: 'auto',
                        },
                    }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            display: 'flex',
                            flexDirection: 'column',
                            overscrollBehavior: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            background: colors.glass.white95,
                            backdropFilter: 'blur(20px)',
                            borderRadius: '0 0 16px 16px',
                            padding: {
                                xs: responsivePadding.container.mobile,
                                sm: responsivePadding.container.tablet,
                            },
                        },
                    }}
                >
                    <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 0.5 }}>
                            <RoleToggle
                                currentRole={user?.user_role || ""}
                                onChange={handleRoleToggle}
                                disabled={!isAuthenticated}
                                loading={isSwitching}
                            />
                        </Box>

                        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.1)', my: 0.5 }} />

                        <CustomButton
                            onClick={handleSignup}
                            buttonText={translations.navigation.signup}
                            fullWidth
                            sx={{
                                py: 1.5,
                                backgroundColor: theme.colors.primary,
                                color: colors.neutral.white,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                fontFamily: theme.typography.bodyText.fontFamily,
                                textTransform: 'none',
                                borderRadius: '8px',
                                '&:hover': {
                                    backgroundColor: colors.button.primaryHover,
                                },
                            }}
                        />
                    </Stack>
                </Drawer>
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setShowSuccessMessage(false)}
                    severity="success"
                    sx={{ width: "100%" }}
                >
                    Role switched successfully!
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={!!roleSwitchError}
                autoHideDuration={5000}
                onClose={clearError}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={clearError}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {roleSwitchError}
                </Alert>
            </Snackbar>

            {/* Notification Dropdown */}
            <NotificationDropdown
                isOpen={Boolean(bellAnchorEl)}
                onClose={handleNotificationClose}
                anchorEl={bellAnchorEl}
            />

            {/* Role Selection Dialog - Shows on home page when user has no role yet */}
            <RoleSelectionDialog
                open={showRoleSelectionDialog}
                onClose={() => {
                    // Don't allow closing until role is selected
                }}
                onRoleSelect={handleInitialRoleSelect}
                loading={isSettingRole}
            />
        </>
    );
});

AuthNavbar.displayName = "AuthNavbar";